import React, { useState } from "react";
import { Calendar, Mail, User, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const textos = [
  // Introdução e orientação geral
  `Para a construção do mesmo foram analisadas as provas do ENEM de 1998 até 2023, 25 anos de prova. Sendo que, com a reformulação do ENEM para adotar uma nova metodologia de análise dos dados através da Teoria de Resposta ao Item, as provas de 2009 para 2023 ganharam um destaque especial, juntamente com os microdados fornecidos pelo INEP para compreensão dos procedimentos realizados na parametrização do certame. Desejamos a todos um excelente passeio pelas estatísticas e reflexões acerca da prova de Humanas do ENEM!`,

  // Como usar o portal
  `COMO USAR ESSE PORTAL?
Instrumentalizar professores da Rede Básica de Ensino na compreensão dos processos que atravessam a construção de itens para o ENEM, em especial os professores da área de HISTÓRIA, bem como fornecer materiais para a melhoria da qualidade metodológica do trabalho dos mesmos é um dos motivos da existência deste portal, mas como usá-lo?`,

  // Seção (1)
  `(1) No Enem, competências são a capacidade de dominar conhecimentos de uma área, enquanto as habilidades são a aplicação prática desses conhecimentos. Na área de Ciências Humanas e suas Tecnologias temos uma estruturação de seis competências, cada uma composta de 5 habilidades, totalizando 30 habilidades ao todo. Todas as provas do ENEM devem conter questões de cada uma das habilidades da matriz. Por isso apresentamos a recorrência de cada uma das habilidades e competências ao longo das provas, para que você, professor(a), possa ter mais clareza de por onde caminhar com seus alunos.`,

  // Seção (2)
  `(2) As questões são parametrizadas dentro da base do ENEM a partir de níveis de dificuldade, variando conforme três, sendo eles: 
- Parâmetro de discriminação (A): é o poder de discriminação que cada questão possui para diferenciar os participantes que dominam dos participantes que não dominam a habilidade avaliada naquela questão.
- Parâmetro de dificuldade (B): associado à dificuldade da habilidade avaliada na questão — quanto maior seu valor, mais difícil é a questão. Ele é expresso na mesma escala da proficiência. Em uma prova de qualidade, devemos ter questões de diferentes níveis de dificuldade para avaliar adequadamente os participantes em todos os níveis de conhecimento.
- Parâmetro de acerto casual (C): em provas de múltipla escolha, um participante que não domina a habilidade avaliada em uma determinada questão da prova pode responder corretamente a um item devido ao acerto casual. Assim, esse parâmetro representa a probabilidade de um participante acertar a questão não dominando a habilidade exigida.

Mas para você, professor de Humanas, será mais tranquilo compreender essa relação de dificuldades com o nosso Quiz de Questões ENEM e com o desdobramento dessas dificuldades em uma lógica Taxonômica, baseada na Taxonomia de Bloom Revisada. Os verbos apresentados se relacionam com palavras para construir uma esfera de domínio cognitivo que compõe o desenvolvimento da aprendizagem histórica e a capacidade do aluno de operar com conceitos para analisar a realidade circundante.`,

  // Seção (3)
  `(3) Você, professor(a), ainda encontrará algumas estatísticas relevantes para seu trabalho, como o grau de interesse de cada componente da área de Humanas por estado, bem como os autores mais recorrentes na prova do ENEM. Os conceitos têm papel chave nesse desdobramento do ensino, por isso a relação deles com os termos históricos, bem como os conceitos mais presentes, nos ajudam a compreender os atravessamentos de conceitos de segunda ordem nos conteúdos substantivos abordados em sala. Bem mais do que aprender sobre a Revolução Francesa ou os tipos de solo, os alunos são requeridos acerca de conceitos que atravessam esses conteúdos, como poder, revolução, movimento, solubilidade, etc.`,

  // Seção (4)
  `(4) A relação dos termos socioculturais e transversais que resvalam na prova de Humanas, como Gênero, Raça, Religião, etc também é apresentada, e fornece uma perspectiva diferenciada para que possam ser trabalhados perpassando as temporalidades.`,

  // Seção (5)
  `(5) Um curso de elaboração de itens, na perspectiva da Teoria de Resposta ao Item, em vídeo é disponibilizado para auxiliar na construção de avaliações internas que sejam instrumentos facilitadores para os desafios que os seus estudantes encontraram fora da sala de aula é disponibilizado gratuitamente.`,

  // Seção (6)
  `(6) Não esqueça de curtir e compartilhar! Por fim, um Raio X dos conteúdos substantivos mais cobrados na prova, bem como indicações de vídeos e áudios que podem enriquecer ainda mais sua prática docente.`,
];

export const Home: React.FC = () => {
  const [showMore, setShowMore] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    if (currentIndex < textos.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  return (
    <section
      id="inicio"
      className="bg-primary flex justify-center bg-no-repeat bg-cover bg-center relative pb-40 z-10"
      style={{
        clipPath: "ellipse(100% 75% at 50% 20%)",
        backgroundImage: `url(${require("../../assets/enem-2021-full.jpg")})`,
      }}
    >
      <div className="absolute inset-0 bg-primary/80 z-0" />
      <div className="mt-20 lg:mb-5 2xl:mb-10 w-full flex flex-col lg:flex-row justify-between items-center lg:max-w-screen-md xl:max-w-screen-lg 2xl:max-w-screen-2xl space-y-10 lg:space-y-0 z-10">
        {/* Texto */}
        <div
          className={`text-white flex flex-col items-start space-y-4 lg:max-w-2xl xl:max-w-2xl ${
            showMore && "mb-6"
          }`}
        >
          <h1 className="text-4xl xl:text-5xl font-semibold leading-snug text-center lg:text-left">
            Humanas no Enem
          </h1>
          <h2 className="text-2xl font-semibold leading-snug text-center lg:text-left">
            O que cai na prova?
          </h2>
          <p className="xl:pt-4 text-sm xl:text-base text-center lg:text-left">
            Esse site é produto da dissertação apresentada no Mestrado
            Profissional em Ensino de História da URCA, visando analisar as
            relações entre o Ensino de História e as provas do ENEM.{" "}
            <AnimatePresence>
              {showMore && (
                <motion.span
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  Para a construção do mesmo foram analisadas as provas do ENEM
                  de 1998 até 2023, 25 anos de prova. Sendo que, com a
                  reformulação do ENEM para adotar uma nova metodologia de
                  análise dos dados através da Teoria de Resposta ao Item, as
                  provas de 2009 para 2023 ganharam um destaque especial,
                  juntamente com os microdados fornecidos pelo INEP para
                  compreensão dos procedimentos realizados na parametrização do
                  certame. Desejamos a todos um excelente passeio pelas
                  estatísticas e reflexões acerca da prova de Humanas do ENEM!
                  COMO USAR ESSE PORTAL ?
                  <>
                    Instrumentalizar professores da Rede Básica de Ensino na
                    compreensão dos processos que atravessam a construção de
                    itens para o ENEM, em especial os professores da área de
                    HISTÓRIA, bem como fornecer materiais para a melhoria da
                    qualidade metodológica do trabalho dos mesmos é um dos
                    motivos da existência deste portal, mas como usa-lo?
                  </>
                  <>
                    (1) No Enem, competências são a capacidade de dominar
                    conhecimentos de uma área, enquanto as habilidades são a
                    aplicação prática desses conhecimentos. Na área de Ciências
                    Humanas e suas Tecnologias temos uma estruturação de seis
                    competências, cada uma composta de 5 habilidades,
                    totalizando 30 habilidades ao todo. Todas as provas do ENEM
                    devem conter questões de cada uma das habilidades da matriz.
                    Por isso apresentamos a recorrência de cada uma das
                    habilidades e competências ao longo das provas, para que
                    você, professor(a), possa ter mais clareza de por onde
                    caminhar com seus alunos.
                  </>
                  <>
                    (2) As questões são parametrizadas dentro da base do ENEM a
                    partir de níveis de dificuldade, variando conforme três,
                    sendo eles: Parâmetro de discriminação (A): é o poder de
                    discriminação que cada questão possui para diferenciar os
                    participantes que dominam dos participantes que não dominam
                    a habilidade avaliada naquela questão. Parâmetro de
                    dificuldade (B): associado à dificuldade da habilidade
                    avaliada na questão — quanto maior seu valor, mais difícil é
                    a questão. Ele é expresso na mesma escala da proficiência.
                    Em uma prova de qualidade, devemos ter questões de
                    diferentes níveis de dificuldade para avaliar adequadamente
                    os participantes em todos os níveis de conhecimento.
                    Parâmetro de acerto casual (C): em provas de múltipla
                    escolha, um participante que não domina a habilidade
                    avaliada em uma determinada questão da prova pode responder
                    corretamente a um item devido ao acerto casual. Assim, esse
                    parâmetro representa a probabilidade de um participante
                    acertar a questão não dominando a habilidade exigida. Mas
                    para você, professor de Humanas, será mais tranquilo
                    compreender essa relação de dificuldades com o nosso Quiz de
                    Questões ENEM e com o desdobramento dessas dificuldades em
                    uma lógica Taxonômica, baseada na Taxonomia de Bloom
                    Revisada. Os verbos apresentados se relacionam com palavras
                    para construir uma esfera de domínio cognitivo que compõe o
                    desenvolvimento da aprendizagem histórica e a capacidade do
                    aluno de operar com conceitos para analisar a realidade
                    circundante.
                  </>
                  <>
                    (3) Você, professor(a), ainda encontrará algumas
                    estatísticas relevantes para seu trabalho, como o grau de
                    interesse de cada componente da área de Humanas por estado,
                    bem como os autores mais recorrentes na prova do ENEM. Os
                    conceitos têm papel chave nesse desdobramento do ensino, por
                    isso a relação deles com os termos históricos, bem como os
                    conceitos mais presentes, nos ajudam a compreender os
                    atravessamentos de conceitos de segunda ordem nos conteúdos
                    substantivos abordados em sala. Bem mais do que aprender
                    sobre a Revolução Francesa ou os tipos de solo, os alunos
                    são requeridos acerca de conceitos que atravessam esses
                    conteúdos, como poder, revolução, movimento, solubilidade,
                    etc.
                  </>
                  <>
                    (4) A relação dos termos socioculturais e transversais que
                    resvalam na prova de Humanas, como Gênero, Raça, Religião,
                    etc também é apresentada, e fornece uma perspectiva
                    diferenciada para que possam ser trabalhados perpassando as
                    temporalidades.
                  </>
                  <>
                    (5) Um curso de elaboração de itens, na perspectiva da
                    Teoria de Resposta ao Item, em vídeo é disponibilizado para
                    auxiliar na construção de avaliações internas que sejam
                    instrumentos facilitadores para os desafios que os seus
                    estudantes encontraram fora da sala de aula é
                    disponibilizado gratuitamente.
                  </>
                  <>
                    (6) Não esqueça de curtir e compartilhar! Por fim, um Raio X
                    dos conteúdos substantivos mais cobrados na prova, bem como
                    indicações de videos e áudios que podem enriquecer ainda
                    mais sua prática docente.
                  </>
                </motion.span>
              )}
            </AnimatePresence>
          </p>
          <button
            className="mt-6 px-6 py-3 bg-white text-primary font-medium rounded-lg shadow-md hover:bg-gray-100"
            onClick={() => {
              setCurrentIndex(0);
              setShowModal(true);
            }}
          >
            Leia mais...
          </button>
        </div>

        {/* Cartões "voando como nuvens" */}
        <div className="relative w-full h-0 md:h-[400px]">
          {/* Cartão 1 */}
          <motion.div
            initial={{ y: 0 }}
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="hidden md:flex absolute top-10 left-10 bg-white/80 p-4 rounded-xl shadow-lg items-center space-x-2 w-64"
          >
            <div className="bg-primary p-2 rounded-full">
              <Calendar className="text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold">1998 a 2023</p>
              <p className="text-xs text-gray-500">
                <a
                  href="https://www.gov.br/inep/pt-br/areas-de-atuacao/avaliacao-e-exames-educacionais/enem/provas-e-gabaritos"
                  className="hover:underline"
                >
                  25 anos de prova
                </a>
              </p>
            </div>
          </motion.div>

          {/* Cartão 2 */}
          <motion.div
            initial={{ y: 0 }}
            animate={{ y: [-5, 5, -5] }}
            transition={{ duration: 5, repeat: Infinity }}
            className="hidden md:flex absolute top-32 right-32 bg-white/80 p-4 rounded-xl shadow-lg items-center space-x-2 w-64"
          >
            <div className="bg-orange-500 p-2 rounded-full">
              <Mail className="text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold">Microdados</p>
              <p className="text-xs text-gray-500">
                <a
                  href="https://www.gov.br/inep/pt-br/acesso-a-informacao/dados-abertos/microdados/enem"
                  className="hover:underline"
                >
                  Microdados fornecidos pelo INEP.
                </a>
              </p>
            </div>
          </motion.div>

          {/* Cartão 3 */}
          <motion.div
            initial={{ y: 0 }}
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 6, repeat: Infinity }}
            className="hidden md:flex absolute bottom-0 left-20 bg-white/80 p-4 rounded-xl shadow-lg items-center space-x-2 w-64"
          >
            <div className="bg-pink-500 p-2 rounded-full">
              <User className="text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold">Estatísticas</p>
              <p className="text-xs text-gray-500">
                Confira as principais estatísticas do Enem
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 z-10 flex items-center justify-center mb-5"
          >
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white max-w-xl w-full rounded-xl p-6 relative"
            >
              <button
                className="absolute top-3 right-3 text-gray-500 hover:text-black"
                onClick={() => setShowModal(false)}
              >
                <X size={20} />
              </button>

              <div className="text-gray-800 space-y-4">
                <h3 className="text-lg font-bold">Seção {currentIndex + 1}</h3>
                <p className="text-sm whitespace-pre-line">
                  {textos[currentIndex]}
                </p>
              </div>

              <div className="mt-6 flex justify-between items-center">
                <button
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={handleNext}
                  disabled={currentIndex === textos.length - 1}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Próximo
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
